import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Account, JournalEntry, FiscalPeriod, TrialBalance,
  AccountType, JournalEntryStatus,
} from '@/types/accounting';

// ─────────── Accounts ───────────

export type AccountFilters = { type?: AccountType; active?: boolean };

export function useAccounts(filters: AccountFilters = {}) {
  return useQuery({
    queryKey: ['accounts', filters],
    queryFn: async () => (await api.get<{ items: Account[] }>('/accounting/accounts', { params: filters })).data.items,
  });
}

export function useAccount(id: string | undefined) {
  return useQuery({
    queryKey: ['account', id],
    queryFn: async () => (await api.get<Account>(`/accounting/accounts/${id}`)).data,
    enabled: !!id,
  });
}

export type SaveAccountInput = Omit<Account, 'id' | 'balance' | 'createdAt' | 'updatedAt'>;

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveAccountInput) =>
      (await api.post<{ id: string }>('/accounting/accounts', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<SaveAccountInput>) =>
      (await api.patch(`/accounting/accounts/${id}`, patch)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['account', vars.id] });
    },
  });
}

export function useSeedChartOfAccounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ created: number }>('/accounting/accounts/seed')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });
}

// ─────────── Journal ───────────

export type JournalFilters = {
  status?: JournalEntryStatus;
  accountId?: string;
  fromDate?: string;
  toDate?: string;
  source?: string;
  limit?: number;
};

export function useJournal(filters: JournalFilters = {}) {
  return useQuery({
    queryKey: ['journal', filters],
    queryFn: async () => (await api.get<{ items: JournalEntry[] }>('/accounting/journal', { params: filters })).data.items,
  });
}

export function useJournalEntry(id: string | undefined) {
  return useQuery({
    queryKey: ['journal-entry', id],
    queryFn: async () => (await api.get<JournalEntry>(`/accounting/journal/${id}`)).data,
    enabled: !!id,
  });
}

export type SaveJournalInput = Omit<JournalEntry,
  'id' | 'number' | 'createdAt' | 'createdBy' | 'createdByName' |
  'updatedAt' | 'postedAt' | 'postedBy' | 'totalDebit' | 'totalCredit' |
  'reversedBy' | 'reverses'
>;

export function useCreateJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveJournalInput) =>
      (await api.post<{ id: string; number: string }>('/accounting/journal', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journal'] }),
  });
}

export function useUpdateJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<SaveJournalInput>) =>
      (await api.patch(`/accounting/journal/${id}`, patch)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['journal'] });
      qc.invalidateQueries({ queryKey: ['journal-entry', vars.id] });
    },
  });
}

export function usePostJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/accounting/journal/${id}/post`)).data,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['journal'] });
      qc.invalidateQueries({ queryKey: ['journal-entry', id] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['trial-balance'] });
    },
  });
}

export function useReverseJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ id: string; number: string }>(`/accounting/journal/${id}/reverse`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['journal'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['trial-balance'] });
    },
  });
}

export function useDeleteJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.delete(`/accounting/journal/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journal'] }),
  });
}

// ─────────── Reports ───────────

export function useTrialBalance(asOf?: string) {
  return useQuery({
    queryKey: ['trial-balance', asOf],
    queryFn: async () =>
      (await api.get<TrialBalance>('/accounting/trial-balance', { params: { asOf } })).data,
  });
}

export type ProfitLoss = {
  fromDate: string;
  toDate: string;
  revenue: Array<{ accountId: string; accountNumber: string; accountName: string; amount: number }>;
  expenses: Array<{ accountId: string; accountNumber: string; accountName: string; amount: number }>;
  totalRevenue: number;
  totalExpenses: number;
  netIncome: number;
};

export function useProfitLoss(fromDate: string, toDate: string) {
  return useQuery({
    queryKey: ['pnl', fromDate, toDate],
    queryFn: async () =>
      (await api.get<ProfitLoss>('/accounting/profit-loss', { params: { fromDate, toDate } })).data,
  });
}

export type BalanceSheet = {
  asOf: string;
  assets: Array<{ accountId: string; accountNumber: string; accountName: string; amount: number }>;
  liabilities: Array<{ accountId: string; accountNumber: string; accountName: string; amount: number }>;
  equity: Array<{ accountId: string; accountNumber: string; accountName: string; amount: number }>;
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  retainedEarnings: number;
  balanced: boolean;
};

export function useBalanceSheet(asOf?: string) {
  return useQuery({
    queryKey: ['balance-sheet', asOf],
    queryFn: async () =>
      (await api.get<BalanceSheet>('/accounting/balance-sheet', { params: { asOf } })).data,
  });
}

// ─────────── Fiscal periods ───────────

export function useFiscalPeriods() {
  return useQuery({
    queryKey: ['fiscal-periods'],
    queryFn: async () =>
      (await api.get<{ items: FiscalPeriod[] }>('/accounting/periods')).data.items,
  });
}

export function useClosePeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/accounting/periods/${id}/close`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fiscal-periods'] }),
  });
}

export function useReopenPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/accounting/periods/${id}/reopen`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fiscal-periods'] }),
  });
}
